import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

export default function DatePickerDD({ value, onChange }) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const calendarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCalendar]);

  const handleInput = (e) => {
    let val = e.target.value.replace(/[^0-9]/g, '');
    
    if (val.length >= 2) val = val.slice(0, 2) + '/' + val.slice(2);
    if (val.length >= 5) val = val.slice(0, 5) + '/' + val.slice(5, 9);
    
    setInputValue(val);
    if (val.length === 10) onChange(val);
  };

  const selectDay = (day) => {
    const formatted = `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${year}`;
    setInputValue(formatted);
    onChange(formatted);
    setShowCalendar(false);
  };

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const today = new Date();
  
  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  return (
    <div className="relative" ref={calendarRef}>
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInput}
          className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500"
          placeholder="DD/MM/YYYY"
          maxLength={10}
          style={{ fontSize: '15px' }}
          required
        />
        <button
          type="button"
          onClick={() => setShowCalendar(!showCalendar)}
          className="absolute right-3 top-1/2 -translate-y-1/2 hover:scale-110 transition-transform"
          style={{ color: '#003594' }}
        >
          <Calendar className="w-5 h-5" />
        </button>
      </div>

      {showCalendar && (
        <div className="absolute top-full mt-2 z-50">
          <div className="bg-white border-2 rounded-2xl shadow-2xl p-4 w-80" style={{ borderColor: '#008AD8' }}>
            <div className="flex items-center justify-between mb-4 pb-3 border-b-2" style={{ borderColor: '#E8F3FA' }}>
              <button
                type="button"
                onClick={() => month === 0 ? (setMonth(11), setYear(year - 1)) : setMonth(month - 1)}
                className="p-2 hover:bg-blue-50 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" style={{ color: '#003594' }} />
              </button>
              
              <div className="text-center">
                <p className="font-bold text-lg" style={{ color: '#003594' }}>
                  {monthNames[month]}
                </p>
                <select
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                  className="text-sm font-semibold border-0 bg-transparent cursor-pointer"
                  style={{ color: '#051C2C' }}
                >
                  {Array.from({length: 10}, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              
              <button
                type="button"
                onClick={() => month === 11 ? (setMonth(0), setYear(year + 1)) : setMonth(month + 1)}
                className="p-2 hover:bg-blue-50 rounded-lg"
              >
                <ChevronRight className="w-5 h-5" style={{ color: '#003594' }} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((d, i) => (
                <div key={i} className="text-center text-xs font-bold text-gray-500 py-2">
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {Array.from({length: firstDay}).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}
              {Array.from({length: daysInMonth}, (_, i) => i + 1).map(day => (
                <button
                  key={day}
                  type="button"
                  onClick={() => selectDay(day)}
                  className={`
                    text-center py-2 text-sm rounded-lg transition-all font-medium
                    ${isToday(day) ? 'bg-blue-600 text-white' : 'hover:bg-blue-100'}
                  `}
                  style={!isToday(day) ? { color: '#051C2C' } : {}}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
